import { Cancel, Delete, Save } from '@mui/icons-material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import CustomButton, {
  CustomTextField,
} from '../../../../Common/Components/Controls/InputControls';
import { Loader } from '../../../../Common/Components/Loader';
import {
  CardWrapper,
  FooterContainer,
  FooterWrapper,
  PaperWrapper,
} from '../../Common/CommonComponents';
import KeyVaultConstants from '../Common/Constants';
import { keyvaultrService } from '../KeyVaultService';
import { SecureKey } from '../Model/KeyVaultModel';
import {
  CreateOperationProps,
  DeleteOperationProps,
  EditOperationProps,
  ParamProps,
} from '../Model/KeyVaultProps';
import { showToast } from '../../../../lib/toast';

const EditOperation: React.FC<EditOperationProps> = ({
  param,
  Value,
  handleEditSave,
  showOperationbutton,
  showBackbutton,
  setValue,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <CardWrapper>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Edit Secure Key
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Key Name</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">{param.KeyName}</Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center" mt={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Key Value</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  value={Value}
                  type={showPassword ? 'text' : 'password'}
                  onChange={(e) => setValue(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <VisibilityIcon />
                          ) : (
                            <VisibilityOffIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  name={''}
                />
              </Grid>
            </Grid>
          </CardWrapper>
        </Grid>
      </Grid>
      <FooterWrapper>
        <FooterContainer>
          <Grid item xs={12} sm="auto">
            {showOperationbutton(handleEditSave)}
          </Grid>
          <Grid item xs={12} sm="auto">
            {showBackbutton()}
          </Grid>
        </FooterContainer>
      </FooterWrapper>
    </>
  );
};

const DeleteOperation: React.FC<DeleteOperationProps> = ({
  param,
  DeleteSecureKey,
  showBackbutton,
}) => {
  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <CardWrapper>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Delete Secure Key
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Key Name</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">{param.KeyName}</Typography>
              </Grid>
            </Grid>
          </CardWrapper>
        </Grid>
      </Grid>
      <FooterWrapper>
        <FooterContainer>
          <Grid item xs={12} sm="auto">
            <CustomButton
              startIcon={<Delete />}
              onClick={DeleteSecureKey}
              label={'Delete'}
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            {showBackbutton()}
          </Grid>
        </FooterContainer>
      </FooterWrapper>
    </>
  );
};

const CreateOperation: React.FC<CreateOperationProps> = ({
  secureKey,
  setSecureKey,
  AddSecureKey,
  showOperationbutton,
  showBackbutton,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
  };

  useEffect(() => {
    const timerId = setTimeout(() => {
      const isValid = /^[A-Za-z0-9-]*$/.test(inputValue);
      setError(!isValid);
      if (isValid) {
        setSecureKey({ ...secureKey, KeyName: inputValue });
      }
    }, 500);

    return () => clearTimeout(timerId);
  }, [inputValue]);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <CardWrapper>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Create Secure Key
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Key Name</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  value={inputValue}
                  onChange={handleInputChange}
                  error={error}
                  helperText={error ? KeyVaultConstants.KeyNameHelperText : ''}
                  name={''}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center" mt={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Key Value</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  type={showPassword ? 'text' : 'password'}
                  value={secureKey.KeyValue}
                  onChange={(e) =>
                    setSecureKey({ ...secureKey, KeyValue: e.target.value })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <VisibilityIcon />
                          ) : (
                            <VisibilityOffIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  name={''}
                />
              </Grid>
            </Grid>
          </CardWrapper>
        </Grid>
      </Grid>
      <FooterWrapper>
        <FooterContainer>
          <Grid item xs={12} sm="auto">
            {showOperationbutton(AddSecureKey)}
          </Grid>
          <Grid item xs={12} sm="auto">
            {showBackbutton()}
          </Grid>
        </FooterContainer>
      </FooterWrapper>
    </>
  );
};

export default function SecureKeyOps() {
  const [secureKey, setSecureKey] = useState<SecureKey>({
    KeyName: '',
    KeyValue: '',
  });
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [Value, setValue] = useState('');
  const param = useParams<ParamProps>();
  const Operation = param.Operation;

  const validateSecureKey = (key: SecureKey) => {
    if (key.KeyName === '') {
      showToast({
        title: KeyVaultConstants.KeyNameError,
        type: 'error',
      });
      return false;
    }
    return true;
  };

  const handleEditSave = async () => {
    try {
      const secureKey: SecureKey = {
        KeyName: param.KeyName,
        KeyValue: Value,
      };
      setLoading(true);
      const key = await keyvaultrService.EditKeyValue(secureKey);
      setValue(key.KeyValue);
      handleCancel();
      showToast({
        title: KeyVaultConstants.KeyEditSuccess,
        type: 'success',
      });
    } catch (error) {
      console.error(error);
      showToast({
        title: (error as Error).message,
        type: 'error',
      });
    }
  };

  const DeleteSecureKey = async () => {
    try {
      setLoading(true);
      const key = await keyvaultrService.DeleteSecureKey(param.KeyName);
      setValue(key);
      handleCancel();
      showToast({
        title: KeyVaultConstants.KeyDelSuccess,
        type: 'success',
      });
    } catch (error) {
      console.error(error);
      const ToastContent: string = (error as Error).message;
      showToast({
        title: ToastContent,
        type: 'error',
      });
    }
  };
  const handleCancel = async () => {
    history.push('/keyvault');
  };

  const AddSecureKey = async () => {
    if (validateSecureKey(secureKey)) {
      try {
        setLoading(true);
        await keyvaultrService.CreateSecureKey(secureKey);
        handleCancel();
        showToast({
          title: KeyVaultConstants.KeySaveSuccess,
          type: 'success',
        });
      } catch (error) {
        console.log(error);
        showToast({
          title: (error as Error).message,
          type: 'error',
        });
      }
    }
  };

  const showBackbutton = () => {
    return (
      <CustomButton
        startIcon={<Cancel />}
        onClick={handleCancel}
        label="Cancel"
      />
    );
  };

  const showOperationbutton = (
    handleOperation: (event: React.MouseEvent<HTMLButtonElement>) => void,
  ) => {
    return (
      <CustomButton
        startIcon={<Save />}
        onClick={handleOperation}
        label="Save"
      />
    );
  };

  return (
    <div>
      <PaperWrapper>
        <Loader loading={loading} />
        <Typography variant="h5" sx={{ mb: 2 }}>
          Key Vault
        </Typography>
        <Divider sx={{ mb: 3 }} />
        {Operation === 'edit' && (
          <EditOperation
            param={param}
            Value={Value}
            handleEditSave={handleEditSave}
            showOperationbutton={showOperationbutton}
            showBackbutton={showBackbutton}
            setValue={setValue}
          />
        )}
        {Operation === 'create' && (
          <CreateOperation
            secureKey={secureKey}
            setSecureKey={setSecureKey}
            AddSecureKey={AddSecureKey}
            showOperationbutton={showOperationbutton}
            showBackbutton={showBackbutton}
          />
        )}
        {Operation === 'delete' && (
          <DeleteOperation
            param={param}
            DeleteSecureKey={DeleteSecureKey}
            showBackbutton={showBackbutton}
          />
        )}
      </PaperWrapper>
    </div>
  );
}
